
import React, { useState, useEffect, useRef } from 'react';
import { StepProgress, StepStatus, UserProfile } from '../types';
import { Check, Lock, ChevronRight, Clock, ShieldCheck, List, FileText, ArrowRight, MessageSquare, AlertTriangle, Send, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

interface StepsPathProps {
    profile: UserProfile;
    onUpdate: (p: UserProfile) => void;
}

const STEPS_CONTENT = [
    { 
        num: 1, 
        shortTitle: "Admitting Powerlessness", 
        fullText: "We admitted we were powerless over alcohol—that our lives had become unmanageable.",
        explainer: "This step is the foundation of recovery, centered on honesty. It asks you to acknowledge two things: first, that you have lost the ability to control your substance use or behavior once you start; and second, that your life has reached a point of chaos or emotional unmanageability because of it.\n\nFor many, this is a moment of \"ego deflation.\" By admitting defeat, you stop the exhausting cycle of trying to \"manage\" an addiction that has proven unmanageable. It isn't about being weak; it is about facing the reality of your situation so that you can finally begin to build a solution on a foundation of truth.",
        defaultHomework: "Read 'The Doctor's Opinion' in the Big Book." 
    },
    { 
        num: 2, 
        shortTitle: "Finding Hope", 
        fullText: "Came to believe that a Power greater than ourselves could restore us to sanity.",
        explainer: "Step 2 is about hope. After admitting you cannot solve the problem alone in Step 1, this step suggests that there is a resource outside of your own willpower that can help. This \"Power greater than ourselves\" is intentionally open-ended; it can be a religious deity, the collective wisdom of the recovery group, nature, or even \"Good Orderly Direction.\"\n\nThe term \"sanity\" in this context refers to the \"insanity\" of the addictive cycle—the repeating of the same harmful behaviors while expecting different results. This step is a mental shift where you become open-minded to the idea that recovery is possible through a source of strength that isn't your own.",
        defaultHomework: "Write a definition of what a Higher Power could look like for you." 
    },
    { 
        num: 3, 
        shortTitle: "Deciding to Trust", 
        fullText: "Made a decision to turn our will and our lives over to the care of God as we understood Him.",
        explainer: "Step 3 is the step of surrender. Having found hope in a Higher Power, you now make a conscious decision to stop trying to \"run the show.\" It is a commitment to let go of self-will—the stubborn demand that the world and other people conform to your desires—and instead try to live in alignment with spiritual principles.\n\nThis \"decision\" is often described as the \"key\" that opens the door to the rest of the work. You don't have to be perfect at it; you just have to be willing to try a new way of living, trusting that by following the program, you will be cared for and guided in a way you couldn't guide yourself.",
        defaultHomework: "Recite the Third Step Prayer each morning." 
    },
    { 
        num: 4, 
        shortTitle: "Self-Examination", 
        fullText: "Made a searching and fearless moral inventory of ourselves.",
        explainer: "This is the action step of self-discovery. It involves taking a written \"inventory\" of your life to identify the patterns, resentments, fears, and behaviors that have kept you trapped in addiction. The goal is to see yourself as you really are, looking at both your strengths and your \"character defects.\"\n\nThe \"fearless\" part is crucial; it requires looking at the parts of your past you might usually hide or justify. By putting it on paper, you take the power away from old secrets and begin to see how your own reactions and choices have contributed to your difficulties.",
        defaultHomework: "Use the digital Inventory Tool to log at least 3 resentments." 
    },
    { 
        num: 5, 
        shortTitle: "Sharing the Truth", 
        fullText: "Admitted to God, to ourselves, and to another human being the exact nature of our wrongs.",
        explainer: "Step 5 is about integrity and breaking the isolation of addiction. You take the inventory you wrote in Step 4 and share it out loud with another person—usually a sponsor or a trusted mentor. This act of \"confession\" helps you see your past objectively and removes the heavy burden of shame.\n\nBy speaking your truth to another person, you realize you are not uniquely \"bad\" or alone. This step provides a sense of profound relief and marks the beginning of true kinship with others. It helps ensure that you aren't just \"dry\" but are actually healing from the inside out.",
        defaultHomework: "Schedule a time with your sponsor to read your Step 4." 
    },
    { 
        num: 6, 
        shortTitle: "Becoming Ready", 
        fullText: "Were entirely ready to have God remove all these defects of character.",
        explainer: "Step 6 is a step of willingness. Now that you know exactly what your character defects are (from Steps 4 and 5), you ask yourself if you are truly ready to let them go. These might include things like anger, pride, greed, or laziness—behaviors that may have served as \"armor\" or coping mechanisms in the past.\n\nThis step is often about letting go of the \"payoff\" we get from our negative traits. It is a quiet, meditative step where you prepare your mind and heart for a different way of being, recognizing that you cannot simply \"will\" these flaws away, but you can become willing to live without them.",
        defaultHomework: "Spend one hour in reflection on your character defects." 
    },
    { 
        num: 7, 
        shortTitle: "Seeking Humility", 
        fullText: "Humbly asked Him to remove our shortcomings.",
        explainer: "Step 7 is the practice of humility. Humility doesn't mean thinking less of yourself; it means thinking of yourself less and having an accurate view of your place in the world. In this step, you specifically ask your Higher Power to take away the flaws that prevent you from being helpful to others and living a peaceful life.\n\nThis is the spiritual opposite of the \"self-will\" found in addiction. You are acknowledging that you need help to change your fundamental nature. It is a daily practice of asking for the strength to be a better version of yourself, one day at a time.",
        defaultHomework: "Recite the Seventh Step Prayer." 
    },
    { 
        num: 8, 
        shortTitle: "Preparing for Amends", 
        fullText: "Made a list of all persons we had harmed, and became willing to make amends to them all.",
        explainer: "Step 8 is the step of willingness to repair relationships. You look back at your life and list the people you have hurt through your addiction—financially, emotionally, or physically. At this stage, you aren't actually talking to them yet; you are simply becoming willing to set things right.\n\nThe focus here is on \"cleaning your side of the street.\" It requires moving past your own resentments toward those people so that you can focus solely on your responsibility for the harm caused. This preparation is essential for the active work that follows in Step 9.",
        defaultHomework: "Draft your list of amends (do not contact them yet)." 
    },
    { 
        num: 9, 
        shortTitle: "Making Amends", 
        fullText: "Made direct amends to such people wherever possible, except when to do so would injure them or others.",
        explainer: "Step 9 is the discipline of taking responsibility. You go to the people on your list and make \"direct amends.\" This is more than a simple \"I'm sorry\"; it is an attempt to repair the damage. This might involve paying back money, admitting the truth, or changing your behavior toward a loved one.\n\nThe \"injury\" clause is vital: you do not make amends if doing so would cause further harm to the other person (for example, confessing an old secret that would destroy their peace of mind). The goal is to clear your conscience and achieve a state of \"peace with the world.\"",
        defaultHomework: "Consult your sponsor, then make one direct amends." 
    },
    { 
        num: 10, 
        shortTitle: "Maintaining Progress", 
        fullText: "Continued to take personal inventory and when we were wrong promptly admitted it.",
        explainer: "Step 10 is the step of perseverance. It takes the work of Steps 4 through 9 and turns it into a daily habit. Recovery is a journey, not a destination, so you must stay alert to new resentments, fears, or selfish behaviors as they crop up in your everyday life.\n\nBy \"promptly admitting\" when you are wrong, you prevent small problems from growing into the kind of massive resentment or guilt that leads back to a relapse. It keeps your spiritual life \"current\" and your relationships healthy in real-time.",
        defaultHomework: "Practice the 'Spot Check' inventory throughout the day." 
    },
    { 
        num: 11, 
        shortTitle: "Spiritual Connection", 
        fullText: "Sought through prayer and meditation to improve our conscious contact with God as we understood Him, praying only for knowledge of His will for us and the power to carry that out.",
        explainer: "Step 11 focuses on spiritual growth. It encourages you to find a daily practice—whether through prayer, meditation, or quiet reflection—to stay connected to your Higher Power. The goal is to move away from asking for \"things\" and instead ask for guidance on how to live and the strength to follow that path.\n\nThis step provides the \"emotional sobriety\" needed for long-term recovery. It helps you stay grounded, calm, and purposeful, ensuring that you have an internal source of strength to handle life’s challenges without turning back to your addiction.",
        defaultHomework: "Complete a Nightly Review." 
    },
    { 
        num: 12, 
        shortTitle: "Service and Action", 
        fullText: "Having had a spiritual awakening as the result of these Steps, we tried to carry this message to alcoholics, and to practice these principles in all our affairs.",
        explainer: "Step 12 is the step of service. It acknowledges that by doing this work, you have experienced a fundamental change in your outlook on life—a \"spiritual awakening.\" The final part of the program is to give back by helping others who are still struggling and to apply these principles (honesty, hope, humility, etc.) to every area of your life.\n\nHelping a newcomer is often described as the best way to stay sober yourself. By living as an example of recovery, you find a new sense of purpose and joy. This step ensures that the program stays alive and that the message of hope is passed on to the next person in need.",
        defaultHomework: "Help a newcomer find a meeting." 
    },
];

export const StepsPath: React.FC<StepsPathProps> = ({ profile, onUpdate }) => {
    const [selectedStep, setSelectedStep] = useState<number | null>(null);
    const [submissionNote, setSubmissionNote] = useState('');
    const [isDescriptionOpen, setIsDescriptionOpen] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);

    // Initial Empty State Check: If profile has no progress, default to Step 1 Active.
    useEffect(() => {
        if (!profile.stepProgress || profile.stepProgress.length === 0) {
            const initProgress: StepProgress[] = Array.from({ length: 12 }, (_, i) => ({
                stepNumber: i + 1,
                status: i === 0 ? 'active' : 'locked',
                homeworkCompleted: false,
                feedbackHistory: []
            }));
            onUpdate({ ...profile, stepProgress: initProgress });
        }
    }, [profile.stepProgress]); // Added dependency to re-run if cleared

    const getProgress = (stepNum: number) => {
        const found = profile.stepProgress?.find(s => s.stepNumber === stepNum);
        if (found) return found;
        // Fallback for empty state UI rendering before useEffect triggers
        return { 
            stepNumber: stepNum, 
            status: stepNum === 1 ? 'active' : 'locked', 
            homeworkCompleted: false, 
            feedbackHistory: [] 
        } as StepProgress;
    };

    // Auto-scroll to active step on load
    useEffect(() => {
        const activeStep = profile.stepProgress?.find(s => s.status === 'active' || s.status === 'pending');
        if (activeStep && containerRef.current) {
            const itemHeight = 85; 
            const containerHeight = 220;
            const index = activeStep.stepNumber - 1;
            const scrollPos = (index * itemHeight) - (containerHeight / 2) + (itemHeight / 2);
            containerRef.current.scrollTo({ top: Math.max(0, scrollPos), behavior: 'smooth' });
        }
    }, [profile.stepProgress]);

    const getDeadlineStatus = (dueDate?: string, status?: StepStatus) => {
        if (!dueDate || status === 'completed' || status === 'locked') return null;
        const due = new Date(dueDate);
        const now = new Date();
        const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) return { label: `${Math.abs(diffDays)}d Overdue`, color: 'text-red-500 bg-red-50 dark:bg-red-900/20', isOverdue: true };
        if (diffDays <= 3) return { label: `${diffDays}d Left`, color: 'text-amber-500 bg-amber-50 dark:bg-amber-900/20', isOverdue: false };
        return { label: `Due ${due.toLocaleDateString(undefined, {month:'short', day:'numeric'})}`, color: 'text-slate-400 bg-slate-100 dark:bg-slate-800', isOverdue: false };
    };

    const handleHomeworkToggle = (stepNum: number) => {
        const updated = profile.stepProgress.map(s => {
            if (s.stepNumber === stepNum) {
                return { ...s, homeworkCompleted: !s.homeworkCompleted };
            }
            return s;
        });
        onUpdate({ ...profile, stepProgress: updated });
    };

    const submitForReview = (stepNum: number) => {
        const updated = profile.stepProgress.map(s => {
            if (s.stepNumber === stepNum) {
                const newFeedback = submissionNote ? [
                    ...s.feedbackHistory,
                    { id: Date.now().toString(), date: new Date().toISOString(), sender: 'user' as const, text: submissionNote }
                ] : s.feedbackHistory;

                return { ...s, status: 'pending' as StepStatus, feedbackHistory: newFeedback };
            }
            return s;
        });
        onUpdate({ ...profile, stepProgress: updated });
        setSubmissionNote('');
    };

    const devSponsorAction = (stepNum: number, action: 'approve' | 'reject') => {
        let updated = profile.stepProgress.map(s => {
            if (s.stepNumber === stepNum) {
                if (action === 'approve') {
                     return { 
                         ...s, 
                         status: 'completed' as StepStatus, 
                         sponsorSignOffDate: new Date().toISOString(),
                         feedbackHistory: [...s.feedbackHistory, { id: Date.now().toString(), date: new Date().toISOString(), sender: 'sponsor' as const, text: 'Great work. Proceed.' }]
                     };
                } else {
                     return { 
                         ...s, 
                         status: 'active' as StepStatus,
                         feedbackHistory: [...s.feedbackHistory, { id: Date.now().toString(), date: new Date().toISOString(), sender: 'sponsor' as const, text: 'Please review the reading again.' }] 
                     };
                }
            }
            return s;
        });
        
        if (action === 'approve') {
            const nextStep = stepNum + 1;
            if (nextStep <= 12) {
                const existingNext = updated.find(s => s.stepNumber === nextStep);
                if (!existingNext) {
                    updated.push({ stepNumber: nextStep, status: 'active', homeworkCompleted: false, feedbackHistory: [] });
                } else if (existingNext.status === 'locked') {
                    updated = updated.map(s => s.stepNumber === nextStep ? { ...s, status: 'active' as StepStatus } : s);
                }
            }
        }

        onUpdate({ ...profile, stepProgress: updated });
    };

    const selectedStepData = selectedStep ? STEPS_CONTENT.find(s => s.num === selectedStep) : null;
    const selectedProgress = selectedStep ? getProgress(selectedStep) : null;
    const deadline = selectedProgress ? getDeadlineStatus(selectedProgress.dueDate, selectedProgress.status) : null;

    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
            <h3 className="text-xl font-bold dark:text-white mb-2">The 12-Step Journey</h3>
            <p className="text-sm text-slate-500 mb-6">Your roadmap to recovery.</p>

            {/* SUBWAY MAP CONTAINER */}
            <div 
                ref={containerRef}
                className="relative pl-4 md:pl-8 py-2 overflow-y-auto h-[220px] scroll-smooth pr-2 custom-scrollbar"
            >
                <div className="space-y-3 pb-4">
                    {STEPS_CONTENT.map((step, index) => {
                        const progress = getProgress(step.num);
                        const status = progress?.status || 'locked';
                        const isSelected = selectedStep === step.num;
                        const dl = getDeadlineStatus(progress?.dueDate, status);

                        let circleColor = 'bg-slate-200 dark:bg-slate-700 text-slate-400';
                        let ringClass = '';
                        
                        if (status === 'completed') circleColor = 'bg-brand-600 text-white';
                        if (status === 'pending') circleColor = 'bg-amber-500 text-white';
                        if (status === 'active') {
                            circleColor = 'bg-white border-4 border-brand-500 text-brand-600';
                            ringClass = 'ring-4 ring-brand-100 dark:ring-brand-900/30';
                            if (dl?.isOverdue) {
                                circleColor = 'bg-white border-4 border-red-500 text-red-600';
                                ringClass = 'ring-4 ring-red-100 dark:ring-red-900/30';
                            }
                        }

                        return (
                            <div 
                                key={step.num} 
                                className={`relative z-10 flex items-center gap-4 cursor-pointer group ${status === 'locked' ? 'opacity-50 pointer-events-none' : ''}`}
                                onClick={() => { setSelectedStep(step.num); setIsDescriptionOpen(true); }}
                            >
                                {/* NODE CIRCLE */}
                                <div className={`relative z-10 w-10 h-10 md:w-14 md:h-14 rounded-full flex items-center justify-center font-bold text-sm md:text-lg transition-all duration-300 flex-shrink-0 ${circleColor} ${ringClass}`}>
                                    {status === 'completed' ? <Check className="w-5 h-5 md:w-7 md:h-7" /> : step.num}
                                    
                                    {/* CONNECTOR LINE SEGMENT 
                                        Logic: Start at center (top-1/2 left-1/2).
                                        Extend downwards to bottom of current row + gap (bottom-[-12px] to account for space-y-3).
                                        Do NOT render on the last item.
                                    */}
                                    {index < STEPS_CONTENT.length - 1 && (
                                        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 w-1 bg-slate-200 dark:bg-slate-700 -z-10 bottom-[-12px]" />
                                    )}
                                </div>

                                {/* LABEL CARD */}
                                <div className={`flex-1 p-3 rounded-xl border transition-all duration-200 ${
                                    isSelected 
                                    ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-700' 
                                    : 'bg-white dark:bg-slate-900 border-transparent hover:bg-slate-50 dark:hover:bg-slate-800'
                                }`}>
                                    <div className="flex justify-between items-center mb-1">
                                        <h4 className={`font-bold ${isSelected ? 'text-brand-700 dark:text-brand-300' : 'text-slate-800 dark:text-slate-200'}`}>
                                            Step {step.num}: {step.shortTitle}
                                        </h4>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-xs flex items-center gap-2">
                                            {status === 'completed' && <span className="text-green-500 font-bold">Done</span>}
                                            {status === 'pending' && <span className="text-amber-500 font-bold flex items-center gap-1"><Clock className="w-3 h-3"/> Pending</span>}
                                            {status === 'active' && <span className="text-brand-600 font-bold">Active</span>}
                                            {status === 'locked' && <Lock className="w-3 h-3 text-slate-400" />}
                                            
                                            {dl && (
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${dl.color}`}>
                                                    {dl.isOverdue && <AlertTriangle className="w-3 h-3" />} {dl.label}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* DETAIL MODAL */}
            {selectedStep && selectedStepData && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedStep(null)}></div>
                    <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-200 flex flex-col max-h-[85vh]">
                        {/* HEADER (Sticky) */}
                        <div className="bg-brand-600 p-6 text-white flex-shrink-0">
                            <div className="flex justify-between items-start">
                                <div>
                                    <span className="inline-block px-2 py-1 rounded bg-white/20 text-xs font-bold mb-2">
                                        Step {selectedStepData.num}
                                    </span>
                                    <h2 className="text-2xl font-bold leading-tight">{selectedStepData.shortTitle}</h2>
                                    <p className="mt-2 text-brand-100 font-serif italic opacity-90 text-sm leading-relaxed border-l-2 border-brand-400 pl-3">
                                        "{selectedStepData.fullText}"
                                    </p>
                                    
                                    {deadline && (
                                        <div className={`mt-3 inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-black/20 text-white`}>
                                            <Clock className="w-3 h-3" /> Deadline: {deadline.label}
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => setSelectedStep(null)} className="text-white/70 hover:text-white">
                                    <div className="bg-black/20 rounded-full p-1">
                                        <ChevronRight className="w-6 h-6 rotate-90 md:rotate-0" />
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* BODY (Scrollable) */}
                        <div className="p-6 space-y-6 overflow-y-auto flex-1 bg-white dark:bg-slate-800 custom-scrollbar">
                            
                            {/* DESCRIPTION ACCORDION */}
                            <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                                <button 
                                    onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
                                    className="w-full flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                >
                                    <h4 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <BookOpen className="w-4 h-4 text-brand-500" /> Description
                                    </h4>
                                    {isDescriptionOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                </button>
                                {isDescriptionOpen && (
                                    <div className="p-4 bg-white dark:bg-slate-800 text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line border-t border-slate-200 dark:border-slate-700">
                                        {selectedStepData.explainer}
                                    </div>
                                )}
                            </div>

                            {/* STEP 4 SPECIAL LINK */}
                            {selectedStepData.num === 4 && selectedProgress?.status !== 'completed' && (
                                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800 flex flex-col items-center text-center gap-3">
                                    <div className="bg-indigo-100 dark:bg-indigo-800 p-3 rounded-full text-indigo-600 dark:text-indigo-300">
                                        <List className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-indigo-900 dark:text-indigo-200">Inventory Tool Available</h4>
                                        <p className="text-sm text-indigo-700 dark:text-indigo-300">Use the digital ledger to track resentments, fears, and harms.</p>
                                    </div>
                                    <Link 
                                        to="/inventory"
                                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                                    >
                                        Open Inventory Ledger <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            )}

                            {/* HOMEWORK SECTION */}
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-brand-500" /> 
                                    {selectedProgress?.assignedHomework ? 'Sponsor Assigned Homework' : 'Suggested Homework'}
                                </h4>
                                <div 
                                    className={`p-4 rounded-xl border cursor-pointer transition-colors flex items-start gap-3 ${
                                        selectedProgress?.homeworkCompleted 
                                        ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                                        : 'bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700 hover:border-brand-300'
                                    }`}
                                    onClick={() => handleHomeworkToggle(selectedStepData.num)}
                                >
                                    <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                                        selectedProgress?.homeworkCompleted
                                        ? 'bg-green-500 border-green-500 text-white'
                                        : 'bg-white border-slate-300 dark:bg-slate-800 dark:border-slate-500'
                                    }`}>
                                        {selectedProgress?.homeworkCompleted && <Check className="w-3 h-3" />}
                                    </div>
                                    <span className={`text-sm ${selectedProgress?.homeworkCompleted ? 'text-green-800 dark:text-green-200 line-through opacity-70' : 'text-slate-700 dark:text-slate-300'}`}>
                                        {selectedProgress?.assignedHomework || selectedStepData.defaultHomework}
                                    </span>
                                </div>
                            </div>
                            
                            {/* FEEDBACK HISTORY */}
                            {selectedProgress && selectedProgress.feedbackHistory.length > 0 && (
                                <div>
                                    <h4 className="font-bold text-slate-900 dark:text-white mb-2 text-xs uppercase tracking-wider flex items-center gap-2">
                                        <MessageSquare className="w-3 h-3" /> Feedback History
                                    </h4>
                                    <div className="space-y-3 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700 max-h-40 overflow-y-auto custom-scrollbar">
                                        {selectedProgress.feedbackHistory.map(msg => (
                                            <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-[85%] p-3 rounded-xl text-xs shadow-sm ${
                                                    msg.sender === 'user' 
                                                    ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-900 dark:text-brand-100 rounded-tr-none' 
                                                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-tl-none'
                                                }`}>
                                                    <div className="font-bold mb-1 text-[10px] opacity-70 flex justify-between gap-4">
                                                        <span>{msg.sender === 'user' ? 'You' : 'Sponsor'}</span>
                                                        <span>{new Date(msg.date).toLocaleDateString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit'})}</span>
                                                    </div>
                                                    <div className="leading-relaxed">{msg.text}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* FOOTER ACTIONS */}
                        <div className="p-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex flex-col gap-2 flex-shrink-0 z-10">
                            {selectedProgress?.status === 'active' && (
                                <>
                                    <div className="relative">
                                        <input 
                                            value={submissionNote}
                                            onChange={e => setSubmissionNote(e.target.value)}
                                            placeholder="Add a note to your sponsor (optional)..."
                                            className="w-full mb-2 p-3 pr-10 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:ring-1 focus:ring-brand-500 outline-none"
                                        />
                                        <Send className="w-4 h-4 text-slate-400 absolute right-3 top-3.5" />
                                    </div>
                                    <button 
                                        onClick={() => submitForReview(selectedStepData.num)}
                                        disabled={!selectedProgress.homeworkCompleted}
                                        className="w-full py-3 rounded-xl font-bold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-brand-500/20"
                                    >
                                        {selectedProgress.homeworkCompleted ? "Submit for Sponsor Review" : "Complete Homework First"}
                                    </button>
                                </>
                            )}
                            
                            {selectedProgress?.status === 'pending' && (
                                <div className="text-center p-2 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-100 dark:border-amber-900/30">
                                    <p className="text-amber-600 dark:text-amber-400 font-bold mb-2 flex items-center justify-center gap-2 text-sm">
                                        <Clock className="w-4 h-4" /> Pending Approval
                                    </p>
                                    <div className="flex gap-4 justify-center text-xs text-slate-400 underline cursor-pointer">
                                        <span onClick={() => devSponsorAction(selectedStepData.num, 'approve')}>[Dev: Approve]</span>
                                        <span onClick={() => devSponsorAction(selectedStepData.num, 'reject')}>[Dev: Reject]</span>
                                    </div>
                                </div>
                            )}

                            {selectedProgress?.status === 'completed' && (
                                <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 p-3 rounded-lg text-center font-bold flex items-center justify-center gap-2">
                                    <ShieldCheck className="w-5 h-5" /> Step Completed
                                </div>
                            )}
                             {selectedProgress?.status === 'locked' && (
                                <div className="text-slate-400 text-center text-sm py-2">
                                    Complete previous steps to unlock.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
